import { Spinner } from '@Pages/pageRuntime/components/ui/Spinner';
import { cn } from '@Pages/pageRuntime/lib/utils';
import { useEffect, useImperativeHandle, useRef } from 'react';

import { Input, NonFileInputTypes } from './Input';

type BaseInputProps = Omit<React.ComponentProps<'input'>, 'onChange' | 'type'>;

export type TextInputProps = BaseInputProps & {
  type?: NonFileInputTypes;
  onChange: (value: string) => void;
  isFocused?: boolean;
  isLoading?: boolean;
  prefixIcon?: React.ElementType;
};

export function TextInput({
  type = 'text',
  className = '',
  isFocused = false,
  isLoading = false,
  prefixIcon,
  value,
  onChange,
  ref,
  ...props
}: TextInputProps & { ref?: React.Ref<HTMLInputElement> }) {
  const localRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => localRef.current as HTMLInputElement, []);

  useEffect(() => {
    if (isFocused) {
      localRef.current?.focus();
    }
  }, [isFocused]);

  const PrefixIcon = prefixIcon as React.ElementType;

  return (
    <div className="relative">
      {prefixIcon ? (
        <div className="absolute inset-y-0 left-0 flex items-center pl-2">
          <PrefixIcon className="size-4 text-secondary-foreground" />
        </div>
      ) : null}
      <Input
        {...props}
        type={type}
        className={cn(isLoading && 'pr-6', prefixIcon && 'pl-8', className)}
        ref={localRef}
        value={value}
        onChange={(value: string) => onChange(value)}
      />
      {isLoading ? (
        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
          <Spinner size="md" color="primary" />
        </div>
      ) : null}
    </div>
  );
}
